import { getLocalePrefix, getRealEstateDict } from '@monoimin/intl';

import { SlugParams, LocaleParams, Locales } from '@monoimin/sb-types';

import { PUBLIC_ESTATE_FILTERS } from '../constants';
import { OBJECT_TYPE_PAGE_SLUG_CONFIGS } from '../configs';
import {
  LocalizedPageSlugConfig,
  ObjectTypePageSlugConfigFiltersState,
  PropertyTypeResponse,
} from '../types';

import { checkEstateExistenseByField } from './checkEstateExistenseByField';

const setActivityToObjectTypes = (
  types: Array<LocalizedPageSlugConfig<ObjectTypePageSlugConfigFiltersState>>,
  params: LocaleParams & Partial<SlugParams<string[]>>
) => {
  const paramsSlug = 'slug' in params ? params.slug : null;

  return types.map((item) => {
    if (Array.isArray(paramsSlug)) {
      const [firstSegment, secondSegment] = paramsSlug;

      return {
        ...item,
        isActive: item.slug === firstSegment || item.slug === secondSegment,
      };
    }

    return { ...item, isActive: false };
  });
};

export const getPropertyTypes = async <L extends Locales>(
  params: LocaleParams<L> & Partial<SlugParams<string[]>>,
  countryCode?: string | undefined,
  countrySlug?: string | undefined
): Promise<Array<PropertyTypeResponse>> => {
  const { locale } = params;

  const realEstateDict = await getRealEstateDict(locale);

  const objectTypesResponse = await checkEstateExistenseByField({
    field: 'object_type',
    filters: {
      ...PUBLIC_ESTATE_FILTERS,
      country: countryCode
        ? {
            code: countryCode,
          }
        : undefined,
    },
    locale: locale,
  });

  if (!objectTypesResponse?.data) {
    return [];
  }

  const localePrefix = getLocalePrefix(locale);

  const countryPrefix = countrySlug ? `/${countrySlug}` : ``;

  const types = OBJECT_TYPE_PAGE_SLUG_CONFIGS.reduce<
    Array<LocalizedPageSlugConfig<ObjectTypePageSlugConfigFiltersState>>
  >((acc, item) => {
    const { slug, filtersState, dictKey } = item;

    const pass = [...filtersState.object_type].every(
      (filter) => objectTypesResponse.data[filter]
    );

    if (!pass) {
      return acc;
    }

    return [
      ...acc,
      {
        title: realEstateDict.objectTypesInPlural[dictKey],
        slug: slug,
        href: `${localePrefix}/real-estate${countryPrefix}/${slug}`,
        filtersState: filtersState,
      },
    ];
  }, []);

  return setActivityToObjectTypes(types, params);
};

export default getPropertyTypes;
